import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { Element } from '../models/Element';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';

import type { RippleConfig } from '../lib/TrackManager';
import type { Track } from '../models/Track';
import type { TrackType } from '../types/TrackType';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command for adding media elements to the timeline with collision detection and track management.
 * Supports different TimelineMode behaviors:
 * - DEFAULT: Creates new track on collision
 * - RIPPLE: Shifts subsequent elements to make room
 * - OVERLAP: Allows overlapping elements
 */
export class AddMediaElementCommand extends AbstractCommand {
  protected element: Element;

  getName(): string {
    return 'AddMediaElementCommand';
  }

  protected targetTrackId?: string;

  protected finalTrackId?: string;

  protected elementAdded: boolean = false;

  protected trackCreated: boolean = false;

  protected createdTrack?: Track;

  protected mode: TimelineMode;

  protected _shouldCreateUndoRedoEntry: boolean;

  protected rippleConfig?: RippleConfig;

  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode: TimelineMode = TimelineMode.DEFAULT,
    shouldCreateUndoRedoEntry: boolean = true,
    rippleConfig?: RippleConfig,
  ) {
    super(editor);
    this.element = element;
    this.targetTrackId = targetTrackId;
    this.mode = mode;
    this._shouldCreateUndoRedoEntry = shouldCreateUndoRedoEntry;
    this.rippleConfig = rippleConfig;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    super.execute(); // Capture timeline snapshot

    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
    });

    // Snap element time to frame (except in OVERLAP mode where precise positioning is required)
    if (this.mode !== TimelineMode.OVERLAP) {
      this.element.update({ time: this.snapTimeToFrame(this.element.time) });
    }

    // Find or create target track
    const targetTrack = this.findOrCreateTargetTrack();
    if (!targetTrack) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to find or create target track',
      });
      return false;
    }

    this.finalTrackId = targetTrack.id;

    // Check for collisions and handle based on mode
    if (!this.handleCollisions(targetTrack)) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to handle collisions',
      });
      return false;
    }

    // Add element to track using TrackManager
    const trackManager = this.editor.getTrackManager();
    const success = trackManager.addElement(
      this.element,
      targetTrack,
      this.mode,
      this.rippleConfig,
    );

    if (success) {
      this.elementAdded = true;
      this.editor.markDirty('add_media_element_command');

      const didShift = this.maybeShiftLogoTracksUp(targetTrack);
      VideoEditorLogger.logTimelineOperation('add_media_element_shift_logo', {
        outcome: didShift ? 'Shifted logo tracks' : 'No shift needed',
        elementId: this.element.id,
        type: this.element.type,
        finalTrackId: this.finalTrackId,
        trackCreated: this.trackCreated,
      });

      // Recalculate timeline duration and reconcile dependent tracks
      this.recalculateTimeline();

      // Ensure proper z-index assignment based on track positions
      this.ensureProperZIndexPositioning();

      this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
        element: this.element,
        trackId: targetTrack.id,
        mode: this.mode,
      });

      VideoEditorLogger.logCommand(this, 'complete', {
        elementId: this.element.id,
        finalTrackId: this.finalTrackId,
        trackCreated: this.trackCreated,
      });
    } else {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'TrackManager failed to add element',
      });
    }

    return success;
  }

  undo(): boolean {
    if (!this.elementAdded) {
      VideoEditorLogger.logCommandUndo(this, false, {
        reason: 'No element to undo',
      });
      return false;
    }

    let success = true;

    try {
      const trackManager = this.editor.getTrackManager();

      // Remove the element using TrackManager
      // Pass rippleConfig to ensure symmetric undo (same overlap/selective logic as execute)
      if (this.finalTrackId) {
        const track = this.editor.getTrackById(this.finalTrackId);
        if (track) {
          const elementRemoved = trackManager.removeElement(
            this.element,
            this.mode,
            this.rippleConfig,
          );
          if (!elementRemoved) {
            success = false;
          }
        } else {
          success = false;
        }
      } else {
        success = false;
      }

      // If we created a track and it's now empty, remove it using TrackManager
      if (success && this.trackCreated && this.createdTrack) {
        const track = this.editor.getTrackById(this.createdTrack.id);
        if (track && track.elements.length === 0) {
          const trackRemoved = trackManager.removeTrack(this.createdTrack);
          if (!trackRemoved) {
            success = false;
          }
        }
      }

      if (success) {
        this.elementAdded = false;
        this.editor.markDirty('add_media_element_command_undo');
      }

      VideoEditorLogger.logCommandUndo(this, success);
      return success;
    } catch (error) {
      VideoEditorLogger.logCommandUndo(this, false, { error });
      return false;
    }
  }

  getDescription(): string {
    const trackInfo = this.trackCreated
      ? `new ${this.element.type} track`
      : `track ${this.finalTrackId || this.targetTrackId || 'auto'}`;
    return `Add ${this.element.type} element to ${trackInfo} (${this.mode} mode)`;
  }

  /**
   * Gets the final track ID where the element was added
   */
  getFinalTrackId(): string | undefined {
    return this.finalTrackId;
  }

  /**
   * Indicates if a new track was created during execution
   */
  getTrackCreated(): boolean {
    return this.trackCreated;
  }

  /**
   * Gets the track that was created (if any)
   */
  getCreatedTrack(): Track | undefined {
    return this.createdTrack;
  }

  /**
   * Finds an appropriate target track or creates a new one
   */
  protected findOrCreateTargetTrack(): Track | undefined {
    // If specific track ID provided, use it (unless it has collisions in DEFAULT mode)
    if (this.targetTrackId) {
      const track = this.editor.getTrackById(this.targetTrackId);
      if (track) {
        const trackManager = this.editor.getTrackManager();

        // For image and composition elements, don't use tracks that are entirely logo tracks
        if (
          (Element.isImageElement(this.element) ||
            Element.isImageCompositionElement(this.element)) &&
          trackManager.isLogoTrack(track)
        ) {
          VideoEditorLogger.logTimelineOperation(
            'add_media_element_logo_track_conflict',
            {
              message: `Target track ${this.targetTrackId} is a logo track, looking for dedicated image track`,
            },
          );

          // For OVERLAP mode, try to find existing dedicated image track
          if (this.mode === TimelineMode.OVERLAP) {
            const dedicatedTrack = this.findDedicatedImageTrack();
            if (dedicatedTrack) {
              VideoEditorLogger.logTimelineOperation(
                'add_media_element_using_dedicated_track',
                {
                  message: `Using existing dedicated image track ${dedicatedTrack.id}`,
                },
              );
              return dedicatedTrack;
            }
          }

          return this.createNewTrack();
        }

        // In DEFAULT mode, if target track has collision, create new track
        if (this.mode === TimelineMode.DEFAULT && this.hasCollision(track)) {
          VideoEditorLogger.logTimelineOperation(
            'add_media_element_collision',
            {
              message: `Target track ${this.targetTrackId} has collision, creating new track`,
            },
          );
          return this.createNewTrack();
        }
        return track;
      } else {
        // Track doesn't exist, create it with the specified ID
        VideoEditorLogger.logTimelineOperation(
          'add_media_element_track_not_found',
          {
            message: `Target track ${this.targetTrackId} not found, creating it`,
          },
        );
        return this.createTrackWithId(this.targetTrackId);
      }
    }

    // Auto-select compatible track based on element type
    const compatibleTrack = this.findCompatibleTrack();
    if (compatibleTrack) {
      return compatibleTrack;
    }

    // Create new track if no compatible track found
    return this.createNewTrack();
  }

  /**
   * Determines the track type for an element based on type and content
   */
  private static getTrackTypeForElement(element: Element): TrackType {
    // special composition type (image with layout, black frame)
    if (Element.isImageCompositionElement(element)) {
      return 'image';
    }

    // Map element types to track types
    const trackTypeMap: Record<string, TrackType> = {
      video: 'video',
      audio: 'audio',
      image: 'image',
      text: 'text',
      composition: 'composition',
      shape: 'shape',
    };

    return trackTypeMap[element.type] || 'video';
  }

  /**
   * Finds an existing track that is dedicated to non-logo image/composition elements.
   * This ensures that image elements are grouped with similar content (e.g. for photo hunt).
   */
  private findDedicatedImageTrack(): Track | undefined {
    if (
      !Element.isImageElement(this.element) &&
      !Element.isImageCompositionElement(this.element)
    ) {
      return undefined;
    }

    const targetTrackType = AddMediaElementCommand.getTrackTypeForElement(
      this.element,
    );

    // Find tracks that contain dedicated image/composition content (non-logo images in OVERLAP mode)
    const dedicatedImageTracks = this.editor.timeline.tracks.filter(track => {
      if (track.type !== targetTrackType) {
        return false;
      }

      // Check if this track is dedicated to image/composition content:
      // 1. Contains non-logo image/composition elements
      // 2. All non-logo elements are images/compositions (no other element types mixed in)
      // 3. Track doesn't contain mixed content types

      const nonLogoImageElements = track.elements.filter(isImageButNotALogo);

      const logoElements = track.elements.filter(el =>
        Element.isLogoElement(el),
      );

      const otherElements = track.elements.filter(
        el =>
          !Element.isImageElement(el) && !Element.isImageCompositionElement(el),
      );

      // Only consider this a dedicated image track if:
      // - It has non-logo images
      // - It has no other element types (video, audio, text, etc.)
      // - It's primarily non-logo images (allowing some logos due to shifting)
      return (
        nonLogoImageElements.length > 0 &&
        otherElements.length === 0 &&
        nonLogoImageElements.length >= logoElements.length
      );
    });

    if (dedicatedImageTracks.length === 0) {
      return undefined;
    }

    // Return the uppermost dedicated image track
    return dedicatedImageTracks.sort((a, b) => {
      const aId = Number(a.id);
      const bId = Number(b.id);
      return bId - aId;
    })[0];
  }

  /**
   * Finds an uppermost compatible existing track for the element
   */
  private findCompatibleTrack(): Track | undefined {
    const targetTrackType = AddMediaElementCommand.getTrackTypeForElement(
      this.element,
    );

    // Find tracks of compatible type
    const compatibleTracks = this.editor.timeline.tracks.filter(track => {
      // Match the target track type
      if (track.type !== targetTrackType) {
        return false;
      }
      if (this.editor.getVideoTranscriptTrack()?.id === track.id) {
        return false; // Exclude transcript track
      }

      // Exclude karaoke track for text elements
      if (
        Element.isTextElement(this.element) &&
        track.id === String(KARAOKE_TRACK_NUMBER)
      ) {
        return false;
      }

      // Exclude logo tracks
      const trackManager = this.editor.getTrackManager();
      if (
        (Element.isImageElement(this.element) ||
          Element.isImageCompositionElement(this.element)) &&
        trackManager.isLogoTrack(track)
      ) {
        return false;
      }

      return true;
    });

    if (compatibleTracks.length === 0) {
      return undefined;
    }

    // For image/composition elements in OVERLAP mode, prefer tracks with existing non-logo images
    if (
      (Element.isImageElement(this.element) ||
        Element.isImageCompositionElement(this.element)) &&
      this.mode === TimelineMode.OVERLAP
    ) {
      const dedicatedImageTrack = compatibleTracks.find(track =>
        track.elements.some(isImageButNotALogo),
      );
      if (dedicatedImageTrack) {
        return dedicatedImageTrack;
      }
    }

    // Sort tracks by ID in descending order to get the uppermost track first
    const sortedTracks = compatibleTracks.sort((a, b) => {
      const aId = Number(a.id);
      const bId = Number(b.id);
      return bId - aId;
    });

    // For DEFAULT mode, find the uppermost track without collisions
    if (this.mode === TimelineMode.DEFAULT) {
      // Iterate from uppermost to lowermost, return first track without collision
      const freeTrack = sortedTracks.find(track => !this.hasCollision(track));
      return freeTrack; // undefined if all compatible tracks have collisions → creates new track
    }

    // For RIPPLE and OVERLAP modes, use the uppermost compatible track
    return sortedTracks[0];
  }

  /**
   * Creates a new track for the element using TrackManager
   */
  protected createNewTrack(): Track | undefined {
    const trackType = AddMediaElementCommand.getTrackTypeForElement(
      this.element,
    );

    try {
      const trackManager = this.editor.getTrackManager();
      this.createdTrack = trackManager.createTrackAboveAll(trackType);
      this.trackCreated = true;

      VideoEditorLogger.logTimelineOperation(
        'add_media_element_track_created',
        {
          action: 'track_created',
          trackId: this.createdTrack.id,
          trackType,
        },
      );

      return this.createdTrack;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to create new track',
        details:
          error instanceof Error
            ? { message: error.message, stack: error.stack }
            : error,
      });
      return undefined;
    }
  }

  /**
   * Creates a track with a specific ID
   */
  protected createTrackWithId(trackId: string): Track | undefined {
    const trackType = AddMediaElementCommand.getTrackTypeForElement(
      this.element,
    );

    try {
      this.createdTrack = this.editor.timeline.createTrack(trackType, trackId);
      this.trackCreated = true;

      VideoEditorLogger.logTimelineOperation(
        'add_media_element_track_created_with_id',
        {
          action: 'track_created_with_id',
          trackId: this.createdTrack.id,
          trackType,
        },
      );

      return this.createdTrack;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: `Failed to create track with ID ${trackId}`,
        details:
          error instanceof Error
            ? { message: error.message, stack: error.stack }
            : error,
      });
      return undefined;
    }
  }

  /**
   * Checks if adding the element to the track would cause a collision
   */
  protected hasCollision(track: Track): boolean {
    const elementStart = this.element.time;
    const elementEnd = this.element.time + this.element.durationSeconds;
    const epsilon = 0.001; // Small tolerance for floating-point comparison

    return track.elements.some(existingElement => {
      const existingStart = existingElement.time;
      const existingEnd =
        existingElement.time + existingElement.durationSeconds;

      // Check for overlap
      return (
        elementStart < existingEnd - epsilon &&
        elementEnd > existingStart + epsilon
      );
    });
  }

  /**
   * Handles collision detection and resolution based on timeline mode
   */
  private handleCollisions(track: Track): boolean {
    switch (this.mode) {
      case TimelineMode.DEFAULT:
        // In DEFAULT mode, we should have already found/created a non-colliding track
        if (this.hasCollision(track)) {
          VideoEditorLogger.logCommand(this, 'error', {
            error: 'Collision detected in DEFAULT mode with selected track',
            trackId: track.id,
          });
          return false;
        }
        return true;

      case TimelineMode.RIPPLE:
        // RIPPLE mode will be handled by TrackManager.addElement
        return true;

      case TimelineMode.RIPPLE_ALL_TRACKS:
        // RIPPLE_ALL_TRACKS mode will be handled by TrackManager.addElement
        return true;

      case TimelineMode.OVERLAP:
        // OVERLAP mode allows collisions
        return true;

      default:
        VideoEditorLogger.logCommand(this, 'error', {
          error: `Unsupported timeline mode: ${this.mode}`,
        });
        return false;
    }
  }

  /**
   * Ensures proper z-index assignment for the newly added element based on track position
   * Uses the same logic as ensureElementZPositions from general.ts
   */
  private ensureProperZIndexPositioning(): void {
    try {
      if (!this.element || !this.finalTrackId) {
        return;
      }

      // Get the track number for z-index assignment
      const trackNumber = parseInt(this.finalTrackId, 10);
      if (Number.isNaN(trackNumber)) {
        VideoEditorLogger.logCommand(this, 'error', {
          message: 'Cannot parse track ID for z-index assignment',
          trackId: this.finalTrackId,
        });
        return;
      }

      // Apply z-index based on track number, following ensureElementZPositions logic
      let zIndex: number;

      // Special case for layout compositions (track >= 90)
      if (Element.isCompositionElement(this.element) && trackNumber >= 90) {
        zIndex = -1000;
      } else {
        // Regular elements get z_index matching their track
        zIndex = trackNumber;
      }

      // Update the element's z_index property
      this.element.update({ z_index: zIndex });

      VideoEditorLogger.logTimelineOperation('z_index_assigned', {
        elementId: this.element.id,
        elementType: this.element.type,
        trackId: this.finalTrackId,
        assignedZIndex: zIndex,
      });
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to ensure z-index positioning',
        details:
          error instanceof Error
            ? { message: error.message, stack: error.stack }
            : error,
      });
    }
  }

  /**
   * Condition1: new topmost track was created for the added element.
   * Condition2: logo tracks are immediately below the new topmost track.
   * If both conditions are met, we should shift logo tracks up.
   *
   * This only applies to topmost consecutive logo tracks, stop shifting when we
   * hit a non-logo track.
   */
  protected maybeShiftLogoTracksUp(targetTrack: Track): boolean {
    const trackManager = this.editor.getTrackManager();

    if (
      !this.trackCreated ||
      !trackManager.isTopmostTrack(targetTrack) ||
      trackManager.isLogoTrack(targetTrack)
    ) {
      return false;
    }

    return this.shiftTrackLayerDownUntilNonLogo(targetTrack);
  }

  private shiftTrackLayerDownUntilNonLogo(targetTrack: Track): boolean {
    const trackManager = this.editor.getTrackManager();
    let prevTrack: Track | undefined = targetTrack;
    let shiftCount = 0;

    while (prevTrack) {
      prevTrack = trackManager.getPrevTrack(prevTrack);
      if (!prevTrack || !trackManager.isLogoTrack(prevTrack)) {
        break;
      }
      shiftCount++;
    }

    for (let i = 0; i < shiftCount; i++) {
      if (trackManager.shiftTrackLayer(targetTrack.id, 'down') == null) {
        return false;
      }
    }

    return true;
  }
}

function isImageButNotALogo(el: Element): boolean {
  if (Element.isLogoElement(el)) return false;
  if (Element.isImageElement(el) || Element.isImageCompositionElement(el)) {
    return true; // Composition elements are not marked as logo
  }
  return false;
}
